/**
 * Decide what to do when (re)arriving at a store (#2016 / diag #44).
 *
 * The task re-spawn bug: returning to a store via "Tap to return"
 * (/store/{id}?projectId=X) remounts StoreDetail, resets visitPhase to
 * 'pending', and the auto-start effect calls startTask(X) again — even when
 * project X's task was already completed in this visit. That produced a task
 * shown as both completed AND active, a blank (lost) survey, and a corrupted
 * "#id #id" label. This helper makes the auto-start consult the REHYDRATED
 * visit state so it starts a task only when one isn't already underway/done.
 *
 * Pure + framework-free so it's unit-testable in isolation. The critical
 * property: an EMPTY visit (a genuine first check-in) always yields `start`,
 * so existing check-in behavior is unchanged — only re-entry diverges.
 */

export type ArrivalAction =
  | { kind: 'start' } // genuinely new — startTask as before
  | { kind: 'active' } // target task is already the current active task — just show it
  | { kind: 'resume'; taskKey: string } // target task is paused — resume it
  | { kind: 'completed' }; // target task already finished this visit — show the picker, don't restart

export interface ArrivalTaskLike {
  task_type?: 'set' | 'call' | string | null;
  task_id?: number;
  project_id?: number;
}

export interface ArrivalTarget {
  taskType?: 'set' | 'call' | string | null;
  /** 0 = synthetic / project-level arrival (no specific set/call id). */
  taskId: number;
  projectId: number;
}

export interface VisitSnapshot {
  currentTask: ArrivalTaskLike | null;
  /** Flattened paused tasks (pass PausedTask.task for each). */
  pausedTasks: ArrivalTaskLike[];
  completedTasks: ArrivalTaskLike[];
}

/** Matches useVisitTask's taskKey(): `${task_type}:${task_id}:${project_id}`. */
export function arrivalTaskKey(t: ArrivalTaskLike): string {
  return `${t.task_type}:${t.task_id}:${t.project_id}`;
}

function matchesTarget(t: ArrivalTaskLike | null | undefined, target: ArrivalTarget): boolean {
  if (!t || t.project_id !== target.projectId) return false;
  // Synthetic task_id 0 (project-level nav-back / picker) is a wildcard on
  // EITHER side: a real-id arrival matches an existing 0-id session for the
  // same project and vice-versa, so re-entry adopts it instead of spawning a
  // duplicate. Two DISTINCT real ids still don't match (separate tasks).
  const ti = t.task_id ?? 0;
  return ti === target.taskId || ti === 0 || target.taskId === 0;
}

export function decideArrivalAction(target: ArrivalTarget, visit: VisitSnapshot): ArrivalAction {
  // Precedence: already-active > paused > completed > new.
  if (matchesTarget(visit.currentTask, target)) return { kind: 'active' };
  const paused = visit.pausedTasks.find((t) => matchesTarget(t, target));
  if (paused) return { kind: 'resume', taskKey: arrivalTaskKey(paused) };
  if (visit.completedTasks.some((t) => matchesTarget(t, target))) return { kind: 'completed' };
  return { kind: 'start' };
}
